import { useId } from 'react'
import { MeasurementInput } from '../../MeasurementInput'
import { useMeasurementPresentation } from '../../Measurement'
import type { AxleDiagramKind } from '../../../lib/vehicleAxleDiagram'
import { equipmentProfileLabels } from './EquipmentSideProfile'

/** Track is measured between wheel centre lines, rather than the outer tyre edges. */
export function EquipmentRearProfile({ kind, width, trackWidth, axleNumber, disabled, onTrackWidthChange, silhouetteTop, inputClassName }: {
  kind: AxleDiagramKind
  width: number
  trackWidth: number | null
  axleNumber: number | null
  disabled: boolean
  onTrackWidthChange: (value: number | null) => void
  silhouetteTop?: number
  inputClassName: string
}) {
  const marker = useId().replace(/:/g, '')
  const { format } = useMeasurementPresentation()
  const knownTrack = trackWidth != null && Number.isFinite(trackWidth)
  const halfTrack = knownTrack ? Math.max(0, Math.min(1, trackWidth / width)) * 74 : 55
  const left = 100 - halfTrack, right = 100 + halfTrack
  return <section data-equipment-rear className="min-w-0 border-slate-200 px-2 py-2 text-slate-500 dark:border-slate-700 dark:text-slate-400">
    <div className="h-9">
      <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-200">Vue arrière</h4>
      <p className="mt-0.5 text-[10px]">{axleNumber == null ? 'Aucun essieu configuré' : `Essieu ${axleNumber}`}</p>
    </div>
    <div className="mx-auto max-w-[200px]" style={{ marginTop: silhouetteTop == null ? 0 : Math.max(0, silhouetteTop - 44) }}>
      <svg data-rear-silhouette viewBox={`0 ${kind === 'tractor' ? 27 : 28} 200 ${kind === 'tractor' ? 193 : 192}`} className="w-full" role="img" aria-label={`Vue arrière : ${equipmentProfileLabels[kind]}${axleNumber == null ? '' : ` · essieu ${axleNumber}`}`} fill="none" stroke="currentColor" strokeWidth="1.2">
        <defs><marker id={marker} viewBox="0 0 8 8" refX="4" refY="4" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M0 0 8 4 0 8" fill="#f97316" stroke="none" /></marker></defs>
        <path d="M12 185H188" className="stroke-slate-200 dark:stroke-slate-800" />
        {kind === 'tractor' ? <g data-rear-cab>
          <path d="M30 150V49Q30 35 44 33L152 27Q168 28 170 44V150Z" className="fill-slate-50 dark:fill-slate-900" />
          <rect x="57" y="52" width="86" height="39" rx="3" className="fill-slate-100 dark:fill-slate-800" />
          <path d="M38 103H162M40 111H160M44 120H156M38 135H162" />
          <path d="M69 146H131V163H69Z" className="fill-slate-300 dark:fill-slate-700" />
        </g> : <g data-rear-cargo>
          <rect x="24" y="28" width="152" height="130" rx={kind === 'van' ? 12 : 2} className="fill-slate-50 dark:fill-slate-900" />
          <path d="M29 33H171V153H29ZM100 34V152M36 141H164" />
          {kind === 'van' ? <><rect x="38" y="43" width="53" height="36" rx="3" className="fill-sky-50 dark:fill-slate-800" /><rect x="109" y="43" width="53" height="36" rx="3" className="fill-sky-50 dark:fill-slate-800" /></> : <path d="M48 42V138M152 42V138M43 82H53M147 82H157M43 117H53M147 117H157" />}
        </g>}
        <path d="M23 158H177V166H23Z" className="fill-slate-200 dark:fill-slate-700" />
        {axleNumber != null && <g data-rear-axle>
          <line x1={left} x2={right} y1="168" y2="168" strokeWidth="3" />
          {[left, right].map((x, index) => <g key={index}>
            <rect x={x - 9} y="151" width="18" height="34" rx="4" className="fill-slate-700 stroke-slate-800 dark:fill-slate-600 dark:stroke-slate-500" />
            <path d={`M${x - 4} 155V181M${x + 4} 155V181`} className="stroke-slate-500 dark:stroke-slate-400" />
            <line x1={x} x2={x} y1="147" y2="210" stroke="#f97316" strokeDasharray="2 3" opacity=".7" />
          </g>)}
          <circle cx="100" cy="168" r="6" className="fill-slate-300 dark:fill-slate-700" />
          {knownTrack && <line data-track-width-arrow x1={left} x2={right} y1="204" y2="204" stroke="#f97316" markerStart={trackWidth > 0 ? `url(#${marker})` : undefined} markerEnd={trackWidth > 0 ? `url(#${marker})` : undefined}>
            <title>Largeur de voie : {format(trackWidth, 'mm', 'vehicleLength')}</title>
          </line>}
        </g>}
        {!knownTrack && <text x="100" y="208" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none">Voie à renseigner</text>}
      </svg>
      <label htmlFor="track-width-input" className="mb-1 block text-center text-[11px] font-medium">Largeur de voie</label>
      <MeasurementInput compact label="Largeur de voie" quantity="vehicleLength" storageUnit="mm" value={trackWidth}
        hint="Distance latérale entre les centres des roues, utilisée pour évaluer la stabilité."
        onValueChange={onTrackWidthChange} id="track-width-input" disabled={disabled} min={0} max={width} placeholder="—"
        className={`${inputClassName} !text-center !pl-8`} />
    </div>
  </section>
}
